import { AccountInfo } from "@azure/msal-browser";
import { messageOf } from "../utils/errorMessage";
import { useCallback, useEffect, useState } from "react";
import { photoStorageService } from "../services/photoStorageService";
import { syncService } from "../services/syncService";
import { Photo, SyncStatus } from "../types/Photo";
import { generateFilename } from "../utils/filenameGenerator";

/** Le plus récent d'abord : c'est l'ordre dans lequel les photos se lisent. */
const newestFirst = (photos: Photo[]): Photo[] =>
  [...photos].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

export const usePhotoQueue = (currentAccount: AccountInfo | null) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sequenceCounter, setSequenceCounter] = useState(1);

  // L'identifiant, pas le compte : MSAL rend un objet neuf à chaque
  // rafraîchissement de jeton, et dépendre de lui relirait la base sans que
  // rien n'ait changé.
  const accountId = currentAccount?.homeAccountId;

  const refreshPhotos = useCallback(async () => {
    if (!accountId) {
      setPhotos([]);
      return;
    }

    setLoading(true);
    try {
      // La purge précède la lecture : la liste ne montre que la fenêtre de
      // sept jours. Qu'elle échoue ne doit pas masquer la file pour autant.
      await photoStorageService
        .purgeExpiredProducts(accountId)
        .catch((err) => console.error("Purge des produits expirés :", err));
      setPhotos(
        newestFirst(await photoStorageService.getPhotosByAccount(accountId))
      );
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  /**
   * Relit la file sans rien annoncer : ni voyant de chargement, ni purge.
   *
   * C'est la relecture des changements d'état d'envoi, qui tombent pendant que
   * la liste est déjà à l'écran. `refreshPhotos` y allumerait `loading` à
   * chaque photo partie, et la liste clignoterait sous l'opérateur.
   */
  const reloadQuietly = useCallback(async () => {
    if (!accountId) return;
    try {
      setPhotos(
        newestFirst(await photoStorageService.getPhotosByAccount(accountId))
      );
    } catch (err) {
      setError(messageOf(err));
    }
  }, [accountId]);

  // Load photos from storage for current account
  useEffect(() => {
    refreshPhotos();
  }, [refreshPhotos]);

  // Les pastilles suivent l'envoi d'elles-mêmes : la file d'envoi prévient,
  // on relit. Aucun sondage périodique — rien ne tourne quand rien ne part.
  useEffect(
    () => syncService.subscribe(() => void reloadQuietly()),
    [reloadQuietly]
  );

  /**
   * Range une photo dans le produit que l'écran de capture a ouvert.
   *
   * Le produit vient de l'appelant, jamais de la file : c'est « Démarrer » ou
   * « Ajouter des photos » qui l'ouvre, et « Produit suivant » qui en ouvre le
   * suivant. Le déduire de la photo la plus récente restée sur l'appareil
   * ferait rejoindre un produit d'hier à la première photo d'aujourd'hui.
   */
  const addPhoto = useCallback(
    async (fullBlob: Blob, thumbnailBlob: Blob, productId: string) => {
      if (!currentAccount) {
        throw new Error("No active account. Cannot save photo.");
      }

      try {
        const now = new Date();
        const id = crypto.randomUUID();
        const filename = generateFilename(now.getTime(), sequenceCounter);
        setSequenceCounter((prev) => prev + 1);

        const newPhoto: Photo = {
          id,
          filename,
          fullBlob,
          thumbnailBlob,
          createdAt: now,
          productId,
          // À la file dès le déclenchement, plus à la clôture de son produit :
          // le temps que l'opérateur passe à cadrer la suivante devient du
          // temps d'envoi, au lieu de s'ajouter à son travail.
          syncStatus: SyncStatus.PENDING,
          accountId: currentAccount.homeAccountId,
          accountEmail: currentAccount.username,
        };

        await photoStorageService.savePhoto(newPhoto);
        setPhotos((prev) => [newPhoto, ...prev]);
        return newPhoto;
      } catch (err) {
        setError(messageOf(err));
        throw err;
      }
    },
    [sequenceCounter, currentAccount]
  );

  const removePhoto = useCallback(async (id: string) => {
    try {
      await photoStorageService.deletePhoto(id);
      setPhotos((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(messageOf(err));
    }
  }, []);

  const clearQueue = useCallback(async () => {
    try {
      await photoStorageService.clearAll();
      setPhotos([]);
    } catch (err) {
      setError(messageOf(err));
    }
  }, []);

  return {
    photos,
    loading,
    error,
    addPhoto,
    removePhoto,
    clearQueue,
    refreshPhotos,
  };
};
